import { useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { api } from '../api';
import { Button } from './ui/button';
import { ErrorNote } from './kit';

/**
 * The console talks HTTP and keeps its own copy of what the API will accept, as
 * it does with every other type. The server is the authority — these two only
 * decide what the picker offers and when it stops offering it.
 */
const ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const ATTACHMENT_MAX_COUNT = 6;

export interface Attached {
  id: string;
  name: string;
  /** A local preview: the bytes are already on the server, but not worth fetching back. */
  preview: string;
}

/** Base64 without the data: prefix, which is what the upload route stores. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`could not read ${file.name}`));
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]+,/, ''));
    reader.readAsDataURL(file);
  });
}

/**
 * Screenshots, uploaded as they are picked rather than when the form is
 * submitted: the ids have to exist before the task does, or a worker can claim a
 * bug report whose pictures have not arrived. An abandoned dialog leaves staged
 * uploads that housekeeping clears.
 */
export function Attachments({
  tenantId,
  value,
  onChange,
}: {
  tenantId: string | undefined;
  value: Attached[];
  onChange: (next: Attached[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const take = async (files: FileList | File[] | null) => {
    const images = [...(files ?? [])].filter((file) => ATTACHMENT_TYPES.includes(file.type));
    if (images.length === 0 || !tenantId) return;
    setBusy(true);
    setError(null);
    try {
      const room = ATTACHMENT_MAX_COUNT - value.length;
      const added: Attached[] = [];
      for (const file of images.slice(0, Math.max(room, 0))) {
        const data = await readAsBase64(file);
        const saved = await api.post<{ id: string; name: string }>('/api/attachments', {
          tenantId,
          name: file.name || 'screenshot.png',
          mediaType: file.type,
          data,
        });
        added.push({ id: saved.id, name: saved.name, preview: `data:${file.type};base64,${data}` });
      }
      onChange([...value, ...added]);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div
      className="grid gap-2"
      // A screenshot is usually already on the clipboard when somebody sits down
      // to report a bug; making them save it to a file first is a step for nothing.
      onPaste={(event) => void take([...event.clipboardData.files])}
    >
      <div className="flex flex-wrap items-center gap-2">
        {value.map((image) => (
          <span key={image.id} className="relative">
            <img
              src={image.preview}
              alt={image.name}
              title={image.name}
              className="size-16 rounded-[5px] border border-rule object-cover"
            />
            <button
              type="button"
              title={`Remove ${image.name}`}
              className="absolute -top-1.5 -right-1.5 rounded-full border border-rule bg-panel p-0.5 text-ink-2 hover:text-ink"
              onClick={() => onChange(value.filter((kept) => kept.id !== image.id))}
            >
              <X className="size-3" aria-hidden />
              <span className="sr-only">Remove {image.name}</span>
            </button>
          </span>
        ))}
        {value.length < ATTACHMENT_MAX_COUNT && (
          <Button type="button" variant="outline" size="sm" disabled={busy || !tenantId} onClick={() => input.current?.click()}>
            <ImagePlus className="size-3.5" aria-hidden /> {busy ? 'Adding…' : 'Add a screenshot'}
          </Button>
        )}
      </div>
      <p className="text-[11.5px] text-ink-3">
        Paste one straight in, or pick a file. The agent reads them as part of the description.
      </p>
      <input
        ref={input}
        type="file"
        accept={ATTACHMENT_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(event) => void take(event.target.files)}
      />
      <ErrorNote error={error} />
    </div>
  );
}
